import { apiSlice } from './apiSlice'

/**
 * Sale Allocation New/Edit/Copy — Access `frmCtrlSalesQrySaleAlcAdd`
 * (table `public.sales_allocation_scs`, PK `sales_allocation_scs_id`).
 *
 * NOTE — BACKEND NOT YET BUILT. The Sales Control list reads
 * `public.mv_sales_allocation_scs` (api_erp_prod.py), but there is no CRUD nor
 * combo-lookup endpoint for the Add form. Every endpoint declared here is a
 * placeholder mirroring the proven `saleDeliveryOrderApi` shape so the dialog can
 * compile and wire today; the URLs marked `TODO BE` must be implemented before
 * this dialog is enabled in production. Lookup queries fall back to `{ items: [] }`
 * (empty combos) until then — no silent fake data.
 *
 * Cache: list query providesTags `SaleControl` (saleControlApi). Mutations here
 * invalidate `SaleControl` so the Sales Allocation datasheet refetches (Access
 * `Forms!frmCtrlSalesMain!navCtrlSales.Form!subForm.Requery`).
 */

/** GET /{id}/form shape — raw columns of one row for Edit/Copy hydrate. */
export interface SaleAllocationForm {
  sales_allocation_scs_id: number
  /** Stored as a single `sale_contract` column; main form splits it into the two
   *  combos by prefix (S* → sale contract, NPE* → consignment). */
  sale_contract: string | null
  delivery_order: string | null
  client: string | null
  contract_no_of_bag: number | null
  contract_quantity: number | null
  m_date: string | null
  m_truck_no: string | null
  m_net_weight: number | null
  from_warehouse: string | null
  to_warehouse: string | null
  container_no: string | null
  seal_no: string | null
  number_bags: number | null
  tare_weight: number | null
  tare_carton_pallet: number | null
  tare_cont: number | null
  max_gross_weight: number | null
  /** Stored receipt_no + gdn_or_rdn discriminator; GDN → Receipt No field,
   *  RDN → Virtual Warehouse field (main form logic). */
  receipt_no: string | null
  gdn_or_rdn: string | null
  export_no_of_bag: number | null
  export_quantity: number | null
  net_weight_at_buyer: number | null
  deduction_at_buyer_kg: number | null
  deduction_at_buyer_vnd: number | null
  allocating_bag: number | null
  allocating_quantity: number | null
  booking_no: string | null
  note: string | null
}

/** New/Edit/Copy write payload — snake_case → sales_allocation_scs columns.
 *  `sale_contract` carries either the S-contract or the consignment contract
 *  (the dialog resolves which, exactly as Access btnSave_Click does). */
export interface SaleAllocationWrite {
  sales_allocation_scs_id?: number | null
  sale_contract?: string | null
  delivery_order?: string | null
  m_date?: string | null
  from_warehouse?: string | null
  to_warehouse?: string | null
  m_truck_no?: string | null
  m_net_weight?: number | null
  container_no?: string | null
  seal_no?: string | null
  number_bags?: number | null
  tare_weight?: number | null
  tare_carton_pallet?: number | null
  tare_cont?: number | null
  max_gross_weight?: number | null
  receipt_no?: string | null
  gdn_or_rdn?: string | null
  net_weight_at_buyer?: number | null
  deduction_at_buyer_kg?: number | null
  deduction_at_buyer_vnd?: number | null
  allocating_bag?: number | null
  allocating_quantity?: number | null
  note?: string | null
}

export interface SaleAlcLookupItem { value: string; label: string }
export interface SaleAlcLookupResponse { items: SaleAlcLookupItem[] }

/** delivery_order_AfterUpdate snapshot — DLookup net_weight_kg / from_warehouse /
 *  place_of_delivery from delivery_order_scs by do_no. */
export interface SaleAlcDoSnapshot {
  contract_quantity: number | null
  from_warehouse: string | null
  to_warehouse: string | null
}

/** receipt_no / virtual_wh AfterUpdate snapshot — DSum bags + quantity, plus the
 *  contract terms (CIF/FOB/FOT) auto-fill of allocating_bag / allocating_quantity. */
export interface SaleAlcReceiptSnapshot {
  export_no_of_bag: number | null
  export_quantity: number | null
  /** Set only when the sale contract delivery_terms is CIF/FOB/FOT (else null). */
  allocating_bag: number | null
  allocating_quantity: number | null
}

const saleAllocationApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // ── frmCtrlSalesQrySaleAlcAdd ─────────────────────────────────────────
    // TODO BE: GET /sale-controls/sale-alc/{id}/form — raw row for Edit/Copy.
    getSaleAllocationForm: builder.query<SaleAllocationForm, number>({
      query: (id) => `/sale-controls/sale-alc/${id}/form`,
    }),
    // btnDelete_Click — soft-delete (is_status = 1). DELETE /sale-controls/sale-alc/{id}.
    deleteSaleAllocation: builder.mutation<{ message: string }, number>({
      query: (id) => ({ url: `/sale-controls/sale-alc/${id}`, method: 'DELETE' }),
      invalidatesTags: ['SaleControl'],
    }),
    // TODO BE: POST /sale-controls/sale-alc — INSERT (btnSave_Click INSERT branch).
    createSaleAllocation: builder.mutation<{ sales_allocation_scs_id: number }, SaleAllocationWrite>({
      query: (body) => ({ url: '/sale-controls/sale-alc', method: 'POST', body }),
      invalidatesTags: ['SaleControl'],
    }),
    // TODO BE: PUT /sale-controls/sale-alc/{id} — UPDATE (btnSave_Click UPDATE branch).
    updateSaleAllocation: builder.mutation<{ sales_allocation_scs_id: number }, { id: number; body: SaleAllocationWrite }>({
      query: ({ id, body }) => ({ url: `/sale-controls/sale-alc/${id}`, method: 'PUT', body }),
      invalidatesTags: ['SaleControl'],
    }),
    // TODO BE: GET /sale-controls/sale-alc/check/seal-no — seal_no uniqueness
    // (seal_no_AfterUpdate + btnSave errCheck 4: DCount over mv_sales_allocation_scs,
    // excluding the current id on Edit).
    checkSaleAllocationSeal: builder.query<{ exists: boolean }, { sealNo: string; excludeId?: number }>({
      query: ({ sealNo, excludeId }) => ({
        url: '/sale-controls/sale-alc/check/seal-no',
        params: { sealNo, ...(excludeId ? { excludeId } : {}) },
      }),
      keepUnusedDataFor: 0,
    }),
    // TODO BE: GET /sale-controls/sale-alc/snapshot/do — delivery_order_AfterUpdate.
    getSaleAllocationDoSnapshot: builder.query<SaleAlcDoSnapshot, string>({
      query: (doNo) => ({ url: '/sale-controls/sale-alc/snapshot/do', params: { doNo } }),
      keepUnusedDataFor: 0,
    }),
    // TODO BE: GET /sale-controls/sale-alc/snapshot/receipt — receipt_no/virtual_wh
    // AfterUpdate (kind = 'gdn' | 'rdn'; saleContract for the CIF/FOB/FOT auto-fill).
    getSaleAllocationReceiptSnapshot: builder.query<
      SaleAlcReceiptSnapshot,
      { kind: 'gdn' | 'rdn'; value: string; saleContract?: string }
    >({
      query: ({ kind, value, saleContract }) => ({
        url: '/sale-controls/sale-alc/snapshot/receipt',
        params: { kind, value, ...(saleContract ? { saleContract } : {}) },
      }),
      keepUnusedDataFor: 0,
    }),

    // ── Combo lookups (Access RowSource queries) ──────────────────────────
    // TODO BE: each returns { items: [{ value, label }] }. Until built they 404 →
    // the dialog guards with `data?.items ?? []` (empty combo, no fake data).

    // sale_contract: SELECT contract_sale, cp_code, name FROM public_mv_local_s.
    saleAlcLookupSaleContracts: builder.query<SaleAlcLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-controls/sale-alc/lookups/sale-contracts', params: q ? { q } : {} }),
    }),
    // consignment_contract: SELECT contract_no, cp_code, name FROM public_v_consignment_s.
    saleAlcLookupConsignments: builder.query<SaleAlcLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-controls/sale-alc/lookups/consignments', params: q ? { q } : {} }),
    }),
    // delivery_order: SELECT do_no FROM public_delivery_order_scs WHERE is_status=0.
    saleAlcLookupDeliveryOrders: builder.query<SaleAlcLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-controls/sale-alc/lookups/delivery-orders', params: q ? { q } : {} }),
    }),
    // from_warehouse / to_warehouse: SELECT IIF(...) AS warehouse_1 FROM ...
    saleAlcLookupWarehouses: builder.query<SaleAlcLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-controls/sale-alc/lookups/warehouses', params: q ? { q } : {} }),
    }),
    // receipt_no: SELECT DISTINCT gdn_id FROM public_v_factory_delivery.
    saleAlcLookupReceiptNos: builder.query<SaleAlcLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-controls/sale-alc/lookups/receipt-nos', params: q ? { q } : {} }),
    }),
    // virtual_wh: SELECT DISTINCT receipt_no FROM public_virtual_warehouse WHERE is_status=0.
    saleAlcLookupVirtualWarehouses: builder.query<SaleAlcLookupResponse, string | void>({
      query: (q) => ({ url: '/sale-controls/sale-alc/lookups/virtual-warehouses', params: q ? { q } : {} }),
    }),
  }),
})

export const {
  useLazyGetSaleAllocationFormQuery,
  useDeleteSaleAllocationMutation,
  useCreateSaleAllocationMutation,
  useUpdateSaleAllocationMutation,
  useLazyCheckSaleAllocationSealQuery,
  useLazyGetSaleAllocationDoSnapshotQuery,
  useLazyGetSaleAllocationReceiptSnapshotQuery,
  useSaleAlcLookupSaleContractsQuery,
  useSaleAlcLookupConsignmentsQuery,
  useSaleAlcLookupDeliveryOrdersQuery,
  useSaleAlcLookupWarehousesQuery,
  useSaleAlcLookupReceiptNosQuery,
  useSaleAlcLookupVirtualWarehousesQuery,
} = saleAllocationApi

export default saleAllocationApi
